import { FC } from 'react';

import { GiftChannelLedgerType } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { ManualLedgerModalState } from '../../hooks/useChannelLedgerPage';

interface Props {
  modal: ManualLedgerModalState;
  setModal: React.Dispatch<React.SetStateAction<ManualLedgerModalState | null>>;
  onSave: () => void;
}

// Deduct는 자동 기장 전용이라 모달로 열리지 않지만 Record 완전성을 위해 채워둔다
const TYPE_COPY: Record<
  GiftChannelLedgerType,
  { title: string; amountLabel: string; hint: string }
> = {
  [GiftChannelLedgerType.Deduct]: {
    title: '발송 차감',
    amountLabel: '차감액 (원)',
    hint: '발송 처리에서 자동 기장됩니다.',
  },
  [GiftChannelLedgerType.Deposit]: {
    title: '충전 입금 기록',
    amountLabel: '입금액 (원)',
    hint: '채널 사이트에 실제 충전한 금액을 그대로 기록합니다 — 잔액에 더해집니다.',
  },
  [GiftChannelLedgerType.ExternalUse]: {
    title: '무관 사용 기록',
    amountLabel: '사용액 (원)',
    hint: '우리 사이트와 무관하게 채널 잔액을 사용한 금액 — 잔액에서 차감됩니다.',
  },
  [GiftChannelLedgerType.Restore]: {
    title: '취소·환급 기록',
    amountLabel: '환급액 (원)',
    hint: '채널 사이트에서 취소·환급받은 금액 — 잔액에 더해집니다. (폐기 후 환급 등)',
  },
  [GiftChannelLedgerType.Adjust]: {
    title: '보정 입력',
    amountLabel: '보정액 (원, ± 가능)',
    hint: '실잔액과의 차이를 강제로 맞춥니다. 도입 시 기초잔액 세팅도 보정으로 입력합니다.',
  },
};

/** 수동 기장 공용 모달 — 입금/무관사용/환급/보정 (type이 부호와 검증을 결정) */
export const ChannelLedgerManualModal: FC<Props> = ({ modal, setModal, onSave }) => {
  const copy = TYPE_COPY[modal.type];
  const isAdjust = modal.type === GiftChannelLedgerType.Adjust;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={() => !modal.isSaving && setModal(null)}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">{copy.title}</h3>
          <p className="mt-1 text-xs text-gray-500">{copy.hint}</p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">채널</label>
            <select
              value={modal.channel}
              onChange={(e) =>
                setModal((prev) =>
                  prev ? { ...prev, channel: e.target.value as DeliveryChannel } : null,
                )
              }
              className="w-full rounded-lg border px-3 py-2 text-sm"
            >
              {DELIVERY_CHANNEL_LIST.map((channel) => (
                <option key={channel} value={channel}>
                  {DELIVERY_CHANNEL_CONFIG[channel].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {copy.amountLabel}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={modal.amount}
              onChange={(e) =>
                setModal((prev) => (prev ? { ...prev, amount: e.target.value } : null))
              }
              placeholder={isAdjust ? '예: 500000 또는 -8500' : '예: 500000'}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
            {!isAdjust && (
              <p className="mt-1 text-[11px] text-gray-400">
                양수로 입력하세요 — 잔액 반영 부호는 유형이 결정합니다.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              발생 시각{' '}
              <span className="font-normal text-gray-400">
                (기본: 지금 — 과거 건은 날짜를 조정하세요)
              </span>
            </label>
            <input
              type="datetime-local"
              value={modal.occurredAt}
              onChange={(e) =>
                setModal((prev) =>
                  prev ? { ...prev, occurredAt: e.target.value } : null,
                )
              }
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              메모{' '}
              {isAdjust ? (
                <span className="text-red-500">(필수)</span>
              ) : (
                <span className="font-normal text-gray-400">(선택)</span>
              )}
            </label>
            <input
              type="text"
              value={modal.memo}
              onChange={(e) =>
                setModal((prev) => (prev ? { ...prev, memo: e.target.value } : null))
              }
              placeholder={
                isAdjust ? '보정 사유 — 예: 기초잔액 세팅' : '예: 7월 4주 충전'
              }
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button
            onClick={() => setModal(null)}
            disabled={modal.isSaving}
            className="rounded-lg border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={onSave}
            disabled={modal.isSaving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {modal.isSaving ? '저장중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};
